// FunctionsManagement.tsx - Updated with Message Banners Control
import React, { useState, useEffect, useCallback } from 'react';
import './FunctionsManagement.css'; 
import { CompanySelection } from './CompanySelection';
import { FunctionForm } from './FunctionForm';
import { FunctionList } from './FunctionList';
import { FunctionsApiService } from './apiService';
import { SuccessMessageBanner } from '../companyManagement/SuccessMessageBanner';
import { ErrorMessageBanner } from '../companyManagement/ErrorMessageBanner';
import { 
  Function, 
  Company, 
  FunctionsManagementProps
} from './types';

// Updated interface to include banner control
interface ExtendedFunctionsManagementProps extends FunctionsManagementProps {
  showInternalBanners?: boolean; // NEW: Control whether to show internal banners
  onManageUsers?: () => void; // NEW: Navigate to user management for current company
  onBack?: () => void;  // ← add this
}

export const FunctionsManagement: React.FC<ExtendedFunctionsManagementProps> = ({
  getAccessToken,
  onStatusUpdate,
  companyContext, // Optional company context
  showInternalBanners = true, // NEW: Default to true for backward compatibility
  onManageUsers ,// NEW: Navigate to user management
  onBack  
}) => {
  // State management
  const [functions, setFunctions] = useState<Function[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [currentView, setCurrentView] = useState<'company-selection' | 'function-list' | 'function-form'>('company-selection');
  const [editingFunction, setEditingFunction] = useState<Function | null>(null);
  
  // Message banner state - only used when showInternalBanners is true
  const [successMessage, setSuccessMessage] = useState({
    isVisible: false,
    message: ''
  });
  
  const [errorMessage, setErrorMessage] = useState({
    isVisible: false,
    message: ''
  });
  
  // API service instance
  const [apiService] = useState(() => new FunctionsApiService(getAccessToken));

  // Handle status updates - display success/error messages
  const handleStatusUpdate = useCallback((status: { type: 'success' | 'error' | null; message: string }) => {
    // Only manage internal banners if showInternalBanners is true
    if (showInternalBanners) {
      // Clear any existing messages first
      setSuccessMessage({ isVisible: false, message: '' });
      setErrorMessage({ isVisible: false, message: '' });
      
      // Show the new message only if type is not null
      if (status.type === 'success') {
        setSuccessMessage({
          isVisible: true,
          message: status.message
        });
      } else if (status.type === 'error') {
        setErrorMessage({
          isVisible: true,
          message: status.message
        });
      }
    }
    
    // Always call the parent's onStatusUpdate if provided
    if (onStatusUpdate) {
      onStatusUpdate(status);
    }
  }, [onStatusUpdate, showInternalBanners]); // Added showInternalBanners to dependencies

  // Initialize based on company context
  useEffect(() => {
    if (companyContext) {
      console.log('Company context provided, skipping company selection:', companyContext);
      
      // Create a company object from context
      const contextCompany: Company = {
        companyId: companyContext.companyId,
        companyName: companyContext.companyName
      };
      
      setSelectedCompany(contextCompany);
      setCurrentView('function-list');
    } else {
      console.log('No company context, showing company selection');
      setCurrentView('company-selection');
    }
  }, [companyContext]);

  // Load companies (only needed when no company context is provided)
  const loadCompanies = useCallback(async () => {
    if (companyContext) {
      // Skip loading companies if we have company context
      return;
    }
    
    setIsLoadingCompanies(true);
    
    const result = await apiService.loadCompanies();
    
    if (result.success && result.data) {
      setCompanies(result.data);
    } else {
      handleStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load companies'
      });
    }
    
    setIsLoadingCompanies(false);
  }, [apiService, handleStatusUpdate, companyContext]);

  // Load functions for selected company
  const loadFunctionsForCompany = useCallback(async (companyId: number) => {
    setIsLoading(true);
    
    const result = await apiService.loadFunctions();
    
    if (result.success && result.data) {
      // Filter functions for the selected company
      const companyFunctions = result.data.filter(func => func.companyId === companyId);
      setFunctions(companyFunctions);
      
      if (result.message) {
        handleStatusUpdate({
          type: 'success',
          message: `Loaded ${companyFunctions.length} functions for ${selectedCompany?.companyName}`
        });
      }
    } else {
      handleStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load functions'
      });
    }
    
    setIsLoading(false);
  }, [apiService, handleStatusUpdate, selectedCompany?.companyName]);

  // Load companies on component mount (only if no company context)
  useEffect(() => {
    if (!companyContext) {
      loadCompanies();
    }
  }, [loadCompanies, companyContext]);

  // Load functions when company is selected
  useEffect(() => {
    if (selectedCompany && currentView === 'function-list') {
      loadFunctionsForCompany(selectedCompany.companyId);
    }
  }, [selectedCompany, currentView, loadFunctionsForCompany]);

  // Handle company selection (only used when no company context)
  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    setCurrentView('function-list');
  };

  // Handle back to company selection (only available when no company context)
  const handleBackToCompanySelection = () => {
    if (companyContext) {
      // Can't go back to company selection when company context is provided
      console.warn('Cannot go back to company selection when company context is provided');
      return;
    }
    
    setSelectedCompany(null);
    setCurrentView('company-selection');
    setFunctions([]);
  };

  // Handle create new function
  const handleCreateNew = () => {
    setEditingFunction(null);
    setCurrentView('function-form');
  };

  // Handle edit function
  const handleEdit = (func: Function) => {
    setEditingFunction(func);
    setCurrentView('function-form');
  };

  // Handle back from form
  const handleBackFromForm = () => {
    setCurrentView('function-list');
    setEditingFunction(null);
  };

  // Handle successful form submission
  const handleFormSuccess = () => {
    setCurrentView('function-list');
    setEditingFunction(null);
    if (selectedCompany) {
      loadFunctionsForCompany(selectedCompany.companyId); // Refresh the list
    }
  };

  // Handle closing success message - only used when showInternalBanners is true
  const handleCloseSuccessMessage = () => {
    setSuccessMessage({ isVisible: false, message: '' });
  };

  // Handle closing error message - only used when showInternalBanners is true
  const handleCloseErrorMessage = () => {
    setErrorMessage({ isVisible: false, message: '' });
  };

  // Render the current view
  const renderCurrentView = () => {
    switch (currentView) {
      case 'company-selection':
        // Only show company selection if no company context is provided
        if (companyContext) {
          // This shouldn't happen, but fallback to function list
          return (
            <FunctionList
              functions={functions}
              selectedCompany={selectedCompany}
              isLoading={isLoading}
              getAccessToken={getAccessToken}
              onStatusUpdate={handleStatusUpdate}
              onEdit={handleEdit}
              onCreateNew={handleCreateNew}
              onRefresh={() => selectedCompany && loadFunctionsForCompany(selectedCompany.companyId)}
              onBackToCompanySelection={handleBackToCompanySelection}
              hideBackButton={!!companyContext} // Hide back button when company context is provided
              onManageUsers={onManageUsers}
            />
          );
        }
        
        return (
          <CompanySelection
            companies={companies}
            isLoadingCompanies={isLoadingCompanies}
            getAccessToken={getAccessToken}
            onStatusUpdate={handleStatusUpdate}
            onCompanySelect={handleCompanySelect}
          />
        );

      case 'function-form':
        return (
          <FunctionForm
            editingFunction={editingFunction}
            companies={companies}
            selectedCompany={selectedCompany}
            isLoadingCompanies={isLoadingCompanies}
            getAccessToken={getAccessToken}
            onStatusUpdate={handleStatusUpdate}
            onBack={handleBackFromForm}
            onSuccess={handleFormSuccess}
          />
        );

      case 'function-list':
      default:
        return (
          <FunctionList
            functions={functions}
            selectedCompany={selectedCompany}
            isLoading={isLoading}
            getAccessToken={getAccessToken}
            onStatusUpdate={handleStatusUpdate}
            onEdit={handleEdit}
            onCreateNew={handleCreateNew}
            onRefresh={() => selectedCompany && loadFunctionsForCompany(selectedCompany.companyId)}
            onBackToCompanySelection={handleBackToCompanySelection}
            hideBackButton={!!companyContext} // Hide back button when company context is provided
            onManageUsers={onManageUsers}
          />
        );
    }
  };

  return (
    <>
      {/* Page Heading */}
      <h1 style={{
        fontSize: '25px',
        fontWeight: 600,
        color: '#1a1a1a',
        margin: '0 0 12px 0',
        padding: '5px 0 0 20px',
        lineHeight: 1.3,
        fontFamily: "'Segoe UI', sans-serif",
        letterSpacing: '-0.01em',
      }}>
        {companyContext 
          ? `Divisions - ${companyContext.companyName}` 
          : 'Division Management'}
      </h1>

      {/* Back link - only when navigated from company context */}
      {companyContext && onBack && (
  <button 
    onClick={onBack}
    className="breadcrumb-link"
    style={{ marginLeft: '20px', marginBottom: '16px' }}
  >
    ← Back to Companies
  </button>
)}

      {/* Main Content */}
      {renderCurrentView()}

      {/* Success Message Banner - Only render if showInternalBanners is true */}
      {showInternalBanners && (
        <SuccessMessageBanner
          message={successMessage.message}
          isVisible={successMessage.isVisible}
          onClose={handleCloseSuccessMessage}
          autoCloseDelay={5000}
        />
      )}

      {/* Error Message Banner - Only render if showInternalBanners is true */}
      {showInternalBanners && (
        <ErrorMessageBanner
          message={errorMessage.message}
          isVisible={errorMessage.isVisible}
          onClose={handleCloseErrorMessage}
          autoCloseDelay={5000}
        />
      )}
    </>
  );
};